/**
 * Phase 1 languages — docs/24_PLAYGROUND_SPEC.md §3.
 * Web-owned presentation metadata.
 * Backend execution policy is consumed from the checked generated contract.
 */

import {
  PLAYGROUND_LANGUAGE_CONTRACT,
  validatePlaygroundLanguageContract,
  type PlaygroundLanguageContract,
  type PlaygroundLanguagePolicy,
} from "./backend-language-contract.generated";

export interface PlaygroundLanguagePresentation {
  id: string;
  name: string;
  monacoLanguage: string;
  fileExtension: string;
  defaultCode: string;
  compilerOptions?: string;
  icon: string;
  seoSlug: string;
}

export type PlaygroundLanguage =
  PlaygroundLanguagePolicy & PlaygroundLanguagePresentation;

export const PLAYGROUND_LANGUAGE_PRESENTATION: readonly PlaygroundLanguagePresentation[] = [
  {
    id: "c",
    name: "C",
    monacoLanguage: "c",
    fileExtension: ".c",
    icon: "lang-c",
    seoSlug: "c",
    defaultCode: `#include <stdio.h>

int main(void) {
    printf("Hello, World!\\n");
    return 0;
}
`,
  },
  {
    id: "cpp",
    name: "C++",
    monacoLanguage: "cpp",
    fileExtension: ".cpp",
    icon: "lang-cpp",
    seoSlug: "cpp",
    // The Playground's default language and template (founder decision
    // 2026-09-03, `PG-UX05`): the competitive-programming opening every
    // student here already types, so the first buffer is the one they expect.
    defaultCode: `#include <bits/stdc++.h>
using namespace std;

int main() {
    cout << "Hello, World!" << endl;
    return 0;
}
`,
  },
  {
    id: "python",
    name: "Python 3",
    monacoLanguage: "python",
    fileExtension: ".py",
    icon: "lang-python",
    seoSlug: "python",
    defaultCode: `print("Hello, World!")
`,
  },
  {
    id: "java",
    name: "Java",
    monacoLanguage: "java",
    fileExtension: ".java",
    icon: "lang-java",
    seoSlug: "java",
    defaultCode: `public class Main {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
    }
}
`,
  },
  {
    id: "javascript",
    name: "JavaScript (Node.js)",
    monacoLanguage: "javascript",
    fileExtension: ".js",
    icon: "lang-javascript",
    seoSlug: "javascript",
    defaultCode: `console.log("Hello, World!");
`,
  },
  {
    id: "typescript",
    name: "TypeScript",
    monacoLanguage: "typescript",
    fileExtension: ".ts",
    icon: "lang-typescript",
    seoSlug: "typescript",
    defaultCode: `const msg: string = "Hello, World!";
console.log(msg);
`,
  },
  {
    id: "go",
    name: "Go",
    monacoLanguage: "go",
    fileExtension: ".go",
    icon: "lang-go",
    seoSlug: "go",
    defaultCode: `package main

import "fmt"

func main() {
	fmt.Println("Hello, World!")
}
`,
  },
  {
    id: "rust",
    name: "Rust",
    monacoLanguage: "rust",
    fileExtension: ".rs",
    icon: "lang-rust",
    seoSlug: "rust",
    defaultCode: `fn main() {
    println!("Hello, World!");
}
`,
  },
  {
    id: "php",
    name: "PHP",
    monacoLanguage: "php",
    fileExtension: ".php",
    icon: "lang-php",
    seoSlug: "php",
    defaultCode: `<?php
echo "Hello, World!\\n";
`,
  },
  {
    id: "ruby",
    name: "Ruby",
    monacoLanguage: "ruby",
    fileExtension: ".rb",
    icon: "lang-ruby",
    seoSlug: "ruby",
    defaultCode: `puts "Hello, World!"
`,
  },
] as const;

export function validatePlaygroundLanguagePresentation(
  contract: PlaygroundLanguageContract,
  presentations: readonly PlaygroundLanguagePresentation[],
): string[] {
  const errors = validatePlaygroundLanguageContract(contract);
  const presentationIds = new Set<string>();

  for (const presentation of presentations) {
    if (presentationIds.has(presentation.id)) {
      errors.push(`Duplicate web presentation key "${presentation.id}".`);
    }
    presentationIds.add(presentation.id);

    if (!presentation.name.trim()) {
      errors.push(`Language "${presentation.id}" is missing a display name.`);
    }
    if (!presentation.monacoLanguage.trim()) {
      errors.push(`Language "${presentation.id}" is missing a Monaco mapping.`);
    }
    if (!presentation.fileExtension.startsWith(".")) {
      errors.push(`Language "${presentation.id}" must have a dotted source extension.`);
    }
    if (!presentation.defaultCode.trim()) {
      errors.push(`Language "${presentation.id}" is missing a default template.`);
    }
    if (!presentation.seoSlug.trim()) {
      errors.push(`Language "${presentation.id}" is missing an SEO slug.`);
    }
  }

  for (const policy of contract.languages) {
    if (!presentationIds.has(policy.id)) {
      errors.push(
        `Language "${policy.id}" is missing web presentation metadata. Sync the catalog and add Monaco, extension, template, icon, name, and slug mappings.`,
      );
    }
  }

  const policyIds = new Set(contract.languages.map(({ id }) => id));
  for (const presentation of presentations) {
    if (!policyIds.has(presentation.id)) {
      errors.push(
        `Web presentation "${presentation.id}" has no backend language policy.`,
      );
    }
  }

  return errors;
}

const presentationErrors = validatePlaygroundLanguagePresentation(
  PLAYGROUND_LANGUAGE_CONTRACT,
  PLAYGROUND_LANGUAGE_PRESENTATION,
);
if (presentationErrors.length > 0) {
  throw new Error(
    `[playground-language-contract]\n${presentationErrors.join("\n")}`,
  );
}

const presentationById = new Map(
  PLAYGROUND_LANGUAGE_PRESENTATION.map((language) => [language.id, language]),
);

export const PLAYGROUND_LANGUAGES: readonly PlaygroundLanguage[] =
  PLAYGROUND_LANGUAGE_CONTRACT.languages
    .filter((policy) => policy.enabled)
    .map((policy) => {
      const presentation = presentationById.get(policy.id);
      if (!presentation) {
        throw new Error(
          `[playground-language-contract] Missing web presentation mapping for "${policy.id}".`,
        );
      }
      return { ...presentation, ...policy };
    });

const byId = new Map(PLAYGROUND_LANGUAGES.map((l) => [l.id, l]));
const bySeoSlug = new Map(PLAYGROUND_LANGUAGES.map((l) => [l.seoSlug, l]));

export function getLanguageById(id: string): PlaygroundLanguage | undefined {
  return byId.get(id);
}

/** Resolve by SEO slug (same as `id` for Phase 1 languages). */
export function getLanguageBySlug(slug: string): PlaygroundLanguage | undefined {
  return bySeoSlug.get(slug) ?? byId.get(slug);
}

const _ids = PLAYGROUND_LANGUAGES.map((l) => l.id);
export const PLAYGROUND_LANGUAGE_IDS = _ids as [string, ...string[]];

/** Params for `generateStaticParams` on `/playground/[language]`. */
export const PLAYGROUND_STATIC_LANGUAGE_PARAMS: { language: string }[] =
  PLAYGROUND_LANGUAGES.map((l) => ({ language: l.seoSlug }));
